import { concat } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { BestSellerScaleType } from '../../../../types/product';
import client from '../../../../graphql/client';
import CustomerType from '../../../../types/customer';
import getProductIdListAndHasBestSeller from '../get-product-id-list-and-has-best-seller';
import getSaleableProductIds from '../../../../utils/get-saleable-product-ids';
import ProductActions from '../../../../actions/product';
import PRODUCTS_WITH_SERVICES from '../../queries/fetch-products-with-services/query';

type useGetSuitableProductIdsProps = {
  customer: {
    scale: BestSellerScaleType;
    hospitalExcess: CustomerType['hospitalExcess'];
  };
  extrasServices: string[];
  hospitalServices: string[];
};

const useGetSuitableProductIds = ({ customer, extrasServices, hospitalServices }: useGetSuitableProductIdsProps) => {
  const hospitalExcess = customer?.hospitalExcess;

  const dispatch = useDispatch();

  const {
    data: extrasData,
    loading: extrasLoading,
    error: extrasError,
  } = useQuery(PRODUCTS_WITH_SERVICES, {
    client,
    variables: {
      input: {
        productType: PRODUCT_TYPE.EXTRAS,
        serviceIds: extrasServices,
      },
    },
  });

  const {
    data: hospitalData,
    loading: hospitalLoading,
    error: hospitalError,
  } = useQuery(PRODUCTS_WITH_SERVICES, {
    client,
    variables: {
      input: {
        productType: PRODUCT_TYPE.HOSPITAL,
        serviceIds: hospitalServices,
      },
    },
  });

  const { productIds: extrasProductIds, hasBestSellerWithFewerInclusions: hasExtrasBestSellerWithFewerInclusions } =
    getProductIdListAndHasBestSeller(
      extrasLoading,
      Boolean(extrasError),
      extrasData,
      PRODUCT_TYPE.EXTRAS,
      customer.scale,
    );
  const {
    productIds: hospitalParentProductIds,
    hasBestSellerWithFewerInclusions: hasHospitalBestSellerWithFewerInclusions,
  } = getProductIdListAndHasBestSeller(
    hospitalLoading,
    Boolean(hospitalError),
    hospitalData,
    PRODUCT_TYPE.HOSPITAL,
    customer.scale,
  );

  const isLoading = extrasLoading || hospitalLoading;
  const hasError = Boolean(extrasError || hospitalError);

  // we need to get all saleable product ids, to allow products unavailable at the current excess to be loaded
  const saleableProductIds = getSaleableProductIds();

  useEffect(() => {
    const productIdsToLoad = concat(extrasProductIds, saleableProductIds);

    const action = ProductActions.load(productIdsToLoad);
    dispatch(action);
  }, [hospitalExcess, isLoading, JSON.stringify(hospitalParentProductIds, extrasProductIds)]);

  return {
    extrasProductIds,
    hasError,
    hasExtrasBestSellerWithFewerInclusions,
    hasHospitalBestSellerWithFewerInclusions,
    hospitalParentProductIds,
    isLoading,
  };
};

export default useGetSuitableProductIds;
